<template>
  <section class="create-flyer-page">
    <form class="create-flyer-shell" @submit.prevent="submitCreateChoice">
      <header class="create-flyer-header">
        <button class="create-flyer-back" type="button" aria-label="Back" @click="handleCreateBack">
          <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">
            <path d="M15 18 9 12l6-6" />
          </svg>
        </button>
        <h1 class="m-0 text-[2rem] font-semibold leading-none text-slate-950">{{ displayPageTitle }}</h1>
      </header>

      <img
        v-if="!isUpdateMode && !creationMode"
        class="creation-type-placeholder"
        :src="flyerPlaceholder"
        alt="Flyer and review preview"
      />

      <fieldset v-if="!isUpdateMode && !creationMode" class="creation-type-card">
        <legend class="creation-type-title">What would you like to share?</legend>
        <div class="creation-type-options">
          <label class="creation-type-option">
            <span class="creation-type-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="4" y="3" width="16" height="18" rx="2" />
                <path d="M8 8h8M8 12h8M8 16h5" />
              </svg>
            </span>
            <span class="min-w-0 flex-1">
              <span class="creation-type-label">Add a flyer</span>
              <span class="creation-type-help">Post a flyer you spotted</span>
            </span>
            <input v-model="creationMode" class="sr-only" type="radio" value="flyer" />
            <svg class="creation-type-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </label>

          <label class="creation-type-option">
            <span class="creation-type-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9L12 3Z" />
              </svg>
            </span>
            <span class="min-w-0 flex-1">
              <span class="creation-type-label">Write a review</span>
              <span class="creation-type-help">Rate a business you visited</span>
            </span>
            <input v-model="creationMode" class="sr-only" type="radio" value="review" />
            <svg class="creation-type-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </label>

          <label class="creation-type-option">
            <span class="creation-type-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M7 10v10" />
                <path d="M4 10h3v10H4a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2Z" />
                <path d="M7 18h9.1a2 2 0 0 0 1.9-1.4l1.7-5A2 2 0 0 0 17.8 9H14l.5-3.1A2.5 2.5 0 0 0 12 3l-1 3.5L7 10" />
              </svg>
            </span>
            <span class="min-w-0 flex-1">
              <span class="creation-type-label">{{ checkinOption.title }}</span>
              <span class="creation-type-help">{{ checkinOption.description }}</span>
            </span>
            <input v-model="creationMode" class="sr-only" type="radio" value="checkin" />
            <svg class="creation-type-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </label>
        </div>
      </fieldset>

      <div v-if="isFlyerMode" class="create-flyer-body">
        <section class="surface-card upload-card">
          <div class="flex items-center justify-between gap-4">
            <span class="text-xl font-medium text-slate-950">Choose Image</span>
            <button class="add-photo-button" type="button" :disabled="images.length >= 5" @click="openAddPhotos">+ Add Photos</button>
          </div>
          <input ref="addPhotosInput" class="hidden" type="file" accept="image/*" multiple @change="addPhotos" />
        </section>

        <section v-if="images.length" class="surface-card grid gap-3">
          <div class="relative overflow-hidden rounded-[1.4rem] bg-slate-100">
            <img class="aspect-[4/3] w-full object-cover" :src="activeImage.src" :alt="`Selected flyer image ${currentSlide + 1}`" />
            <button class="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/95 text-slate-700 shadow" type="button" aria-label="Remove image" @click="removeImage(currentSlide)">
              <svg class="h-4.5 w-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
          <div class="flex items-center justify-center gap-2">
            <button
              v-for="(image, index) in images"
              :key="image.id"
              class="h-2.5 rounded-full transition"
              :class="currentSlide === index ? 'w-7 bg-blue-600' : 'w-2.5 bg-slate-300'"
              type="button"
              :aria-label="`Show image ${index + 1}`"
              @click="currentSlide = index"
            />
          </div>
          <p class="m-0 text-center text-xs font-normal text-slate-500">{{ images.length }} / 5 images</p>
        </section>

        <section class="surface-card">
          <RichTextEditor
            v-model="form.description"
            :invalid="Boolean(errors.description)"
            placeholder="What did you spot today?"
            @blur="validateField('description')"
          />
          <p v-if="errors.description" class="inline-error">{{ errors.description }}</p>
        </section>

        <section class="content-section">
          <p class="section-label">Details</p>
          <div class="surface-card surface-stack">
            <div class="detail-row detail-row-static">
              <span class="row-icon-square">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                  <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </span>
              <div class="min-w-0 flex-1">
                <h2 class="m-0 text-[1.05rem] font-medium text-slate-950">Flyer Location*</h2>
                <p class="m-0 text-sm font-normal text-slate-500">Where is this flyer from?</p>
                <p class="m-0 mt-3 text-lg font-medium leading-7 text-slate-950">{{ form.flyerLocation || 'Choose a Location' }}</p>
                <p v-if="errors.flyerLocation" class="inline-error">{{ errors.flyerLocation }}</p>
              </div>
              <button class="link-button" type="button" @click="openLocationPicker">Change</button>
            </div>

            <div
              class="detail-row"
              role="button"
              tabindex="0"
              @click="dateModalOpen = true"
              @keydown.enter="dateModalOpen = true"
              @keydown.space.prevent="dateModalOpen = true"
            >
              <span class="row-icon-square">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                  <path d="M8 2v4" />
                  <path d="M16 2v4" />
                  <path d="M3 10h18" />
                  <path d="M5 5h14a2 2 0 0 1 2 2v12H3V7a2 2 0 0 1 2-2Z" />
                </svg>
              </span>
              <span class="min-w-0 flex-1 text-left">
                <span class="block text-[1.05rem] font-medium text-slate-950">Sale dates</span>
                <span class="block truncate text-sm font-normal text-slate-500">{{ dateSummary }}</span>
                <span v-if="errors.dates" class="inline-error">{{ errors.dates }}</span>
              </span>
              <button
                v-if="form.startsDate || form.endsDate"
                class="clear-date-link shrink-0"
                type="button"
                @click.stop="clearDateRange"
              >
                Clear
              </button>
              <ChevronIcon />
            </div>
          </div>
        </section>

        <section class="content-section">
          <p class="section-label">Audience</p>
          <div class="surface-card grid gap-4">
            <div>
              <h2 class="m-0 text-[1.05rem] font-medium text-slate-950">Target Audience Location*</h2>
              <p class="m-0 text-sm font-normal text-slate-500">who should see this flyer?</p>
              <p v-if="errors.audience" class="inline-error">{{ errors.audience }}</p>
            </div>
            <div class="audience-chip-grid">
              <button
                v-for="country in countries"
                :key="country"
                class="audience-chip"
                :class="{ 'audience-chip-active': form.countries.includes(country) }"
                type="button"
                @click="toggleAudienceCountry(country)"
              >
                {{ country }}
              </button>
              <button class="audience-chip" :class="{ 'audience-chip-active': form.audienceSameAsFlyer }" type="button" @click="selectSameAsFlyer">
                Same as flyer
              </button>
            </div>
          </div>
        </section>

        <section class="content-section">
          <p class="section-label">Sharing</p>
          <div class="surface-card surface-stack">
            <button class="detail-row" type="button" @click="visibilityModalOpen = true">
              <span class="min-w-0 flex-1 text-left">
                <span class="block text-[1.05rem] font-medium text-slate-950">Who can see My Flyer</span>
                <span class="mt-1 block text-lg font-medium text-slate-950">{{ form.visibility }}</span>
                <span class="block text-sm font-normal text-slate-400">{{ visibilityDescription }}</span>
              </span>
              <ChevronIcon />
            </button>

            <button class="detail-row" type="button" @click="openBoardModal">
              <span class="min-w-0 flex-1 text-left">
                <span class="block text-[1.05rem] font-medium text-slate-950">Boards*</span>
                <span class="block text-sm font-normal text-slate-500">{{ form.board || 'Select Board' }}</span>
                <span v-if="errors.board" class="inline-error">{{ errors.board }}</span>
              </span>
              <ChevronIcon />
            </button>

            <button class="detail-row" type="button" @click="contentTypeModalOpen = true">
              <span class="min-w-0 flex-1 text-left">
                <span class="block text-[1.05rem] font-medium text-slate-950">Content Type*</span>
                <span class="block text-sm font-normal text-slate-500">{{ form.contentType || 'Select Content Type' }}</span>
                <span v-if="errors.contentType" class="inline-error">{{ errors.contentType }}</span>
              </span>
              <ChevronIcon />
            </button>
          </div>
        </section>

        <div class="pb-6">
          <div v-if="submitError" class="mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm font-normal text-red-700">
            {{ submitError }}
          </div>
          <button class="post-button" type="submit" :disabled="isSubmitting">
            {{ isSubmitting ? 'Saving...' : submitLabel }}
          </button>
        </div>
      </div>

      <div v-else-if="!isUpdateMode && isBusinessPostMode && !selectedBusiness" class="create-flyer-body review-business-step">
        <section class="surface-card">
          <div class="business-search-field">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-4-4" />
            </svg>
            <input v-model="businessSearch" type="search" placeholder="Search business" autocomplete="off" @input="handleBusinessSearch" />
            <button v-if="businessSearch" type="button" aria-label="Clear business search" @click="clearBusinessSearch">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <button class="business-list-heading w-full text-left" type="button" @click="openReviewLocationPicker">
            <span class="business-list-heading-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
                <circle cx="12" cy="10" r="2.5" />
              </svg>
            </span>
            <span class="min-w-0 flex-1">
              <span class="block truncate text-base font-medium text-slate-950">Select Google Business</span>
              <span class="block truncate text-xs font-normal text-slate-400">Choose a location to find businesses</span>
            </span>
            <i class="ri-arrow-right-s-line shrink-0 text-xl text-slate-400" aria-hidden="true"></i>
          </button>

          <div v-if="businessesLoading || businessSearchLoading" class="grid gap-3" aria-live="polite">
            <div v-for="index in 4" :key="index" class="business-row business-row-skeleton">
              <span class="business-avatar-skeleton"></span>
              <span class="grid flex-1 gap-2">
                <span class="business-line-skeleton business-line-skeleton-wide"></span>
                <span class="business-line-skeleton"></span>
              </span>
            </div>
          </div>

          <div v-else-if="visibleBusinessError" class="review-state-message review-state-error">
            <p class="m-0">{{ visibleBusinessError }}</p>
            <button class="link-button mt-2" type="button" @click="retryBusinessLoad">Try again</button>
          </div>

          <div v-else-if="!filteredBusinesses.length" class="review-state-message">
            {{ businessSearch ? 'No businesses match your search.' : 'No businesses are available for your current location.' }}
          </div>

          <div v-else class="business-list">
            <button v-for="business in filteredBusinesses" :key="business.id" class="business-row w-full text-left" type="button" @click="selectBusiness(business)">
              <span class="business-avatar business-avatar-outline">
                <img v-if="business.image" :src="business.image" :alt="business.name" />
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                  <circle cx="12" cy="7" r="4" />
                  <path d="M4 22v-2a8 8 0 0 1 16 0v2" />
                </svg>
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-medium leading-5 text-slate-950">{{ business.name }}</span>
                <span v-if="business.meta" class="mt-0.5 block text-xs font-normal leading-4 text-slate-500">{{ business.meta }}</span>
              </span>
              <i class="ri-arrow-right-s-line shrink-0 text-xl text-slate-400" aria-hidden="true"></i>
            </button>
          </div>
        </section>
      </div>

      <div v-else-if="isBusinessPostMode && selectedBusiness" class="create-flyer-body review-form-step">
        <section class="surface-card selected-business-card">
          <div class="flex min-w-0 items-center gap-3">
            <span class="business-avatar business-avatar-outline">
              <img v-if="selectedBusiness.image" :src="selectedBusiness.image" :alt="selectedBusiness.name" />
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <circle cx="12" cy="7" r="4" />
                <path d="M4 22v-2a8 8 0 0 1 16 0v2" />
              </svg>
            </span>
            <span class="min-w-0 flex-1">
              <span class="block text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-blue-600">Selected business</span>
              <span class="block text-sm font-medium leading-5 text-slate-950">{{ selectedBusiness.name }}</span>
              <span v-if="selectedBusiness.address || selectedBusiness.meta" class="mt-0.5 block text-xs font-normal leading-4 text-slate-500">
                {{ selectedBusiness.address || selectedBusiness.meta }}
              </span>
            </span>
          </div>
          <button v-if="!isUpdateMode" class="link-button shrink-0 self-center" type="button" @click="changeBusiness">Change</button>
        </section>

        <section v-if="creationMode === 'review'" class="surface-card review-rating-card">
          <fieldset class="review-rating-fieldset">
            <legend class="text-base font-medium text-slate-950">Rating</legend>
            <div class="rating-stars" role="radiogroup" aria-label="Business rating">
              <label v-for="star in 5" :key="star" class="rating-star" :class="{ 'rating-star-active': star <= reviewForm.rating }">
                <input v-model.number="reviewForm.rating" class="sr-only" type="radio" name="rating" :value="star" />
                <svg viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" aria-hidden="true">
                  <path d="m12 2.8 2.85 5.78 6.38.93-4.62 4.5 1.09 6.35L12 17.37l-5.7 3 1.09-6.35-4.62-4.5 6.38-.93L12 2.8Z" />
                </svg>
              </label>
            </div>
            <p v-if="reviewErrors.rating" class="inline-error">{{ reviewErrors.rating }}</p>
          </fieldset>
        </section>

        <section class="surface-card">
          <label class="review-label">{{ creationMode === 'checkin' ? 'Comment' : 'Review' }}</label>
          <RichTextEditor
            v-model="form.description"
            :invalid="Boolean(reviewErrors.review)"
            :placeholder="creationMode === 'checkin' ? 'Write a comment' : 'Your Review'"
            @blur="validateReview"
          />
          <p v-if="reviewErrors.review" class="inline-error">{{ reviewErrors.review }}</p>
        </section>

        <section v-if="creationMode === 'review'" class="surface-card upload-card">
          <div class="flex items-center justify-between gap-4">
            <span class="text-xl font-medium text-slate-950">Choose Image</span>
            <button class="add-photo-button" type="button" :disabled="images.length >= 5" @click="openAddPhotos">+ Add Photos</button>
          </div>
          <input ref="addPhotosInput" class="hidden" type="file" accept="image/*" multiple @change="addPhotos" />
        </section>

        <section v-if="creationMode === 'review' && images.length" class="surface-card grid gap-3">
          <div class="relative overflow-hidden rounded-[1.4rem] bg-slate-100">
            <img class="aspect-[4/3] w-full object-cover" :src="activeImage.src" :alt="`Selected ${creationMode === 'checkin' ? 'checkin' : 'review'} image ${currentSlide + 1}`" />
            <button class="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/95 text-slate-700 shadow" type="button" aria-label="Remove image" @click="removeImage(currentSlide)">
              <svg class="h-4.5 w-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="flex items-center justify-center gap-2">
            <button
              v-for="(image, index) in images"
              :key="image.id"
              class="h-2.5 rounded-full transition"
              :class="currentSlide === index ? 'w-7 bg-blue-600' : 'w-2.5 bg-slate-300'"
              type="button"
              :aria-label="`Show image ${index + 1}`"
              @click="currentSlide = index"
            />
          </div>
          <p class="m-0 text-center text-xs font-normal text-slate-500">{{ images.length }} / 5 images</p>
        </section>

        <div class="pb-6">
          <div v-if="reviewSubmitError" class="mb-3 rounded-xl bg-red-50 px-3 py-2 text-sm font-normal text-red-700">
            {{ reviewSubmitError }}
          </div>
          <button class="post-button" type="submit" :disabled="isReviewSubmitting">
            {{ isReviewSubmitting ? (isUpdateMode ? 'Updating...' : 'Posting...') : submitLabel }}
          </button>
        </div>
      </div>
    </form>

    <BottomSheet v-if="countriesModalOpen" title="Countries" @close="countriesModalOpen = false">
      <div class="divide-y divide-slate-200">
        <label v-for="country in countries" :key="country" class="modal-choice">
          <span>{{ country }}</span>
          <input v-model="countryDraft" class="h-6 w-6 accent-blue-600" type="checkbox" :value="country" />
        </label>
      </div>
      <div class="grid gap-4 px-5 py-5 sm:grid-cols-2">
        <button class="modal-secondary" type="button" @click="countriesModalOpen = false">Cancel</button>
        <button class="modal-primary" type="button" @click="applyCountries">Submit</button>
      </div>
    </BottomSheet>

    <BottomSheet v-if="dateModalOpen" title="Sale / offer date" @close="dateModalOpen = false">
      <div class="grid gap-4 px-5 py-5 sm:grid-cols-2">
        <label class="field-label">
          <span>Starts date</span>
          <input v-model="form.startsDate" class="field-input" type="date" :min="todayDate" @change="syncEndDateFromStart" />
          <span v-if="errors.dates" class="inline-error">{{ errors.dates }}</span>
        </label>
        <label class="field-label">
          <span>Ends date</span>
          <input v-model="form.endsDate" class="field-input" type="date" :min="endDateMin" />
        </label>
      </div>
      <div class="grid gap-4 px-5 pb-5 sm:grid-cols-2">
        <button class="modal-secondary" type="button" @click="dateModalOpen = false">Cancel</button>
        <button class="modal-primary" type="button" @click="dateModalOpen = false">Apply</button>
      </div>
    </BottomSheet>

    <BottomSheet v-if="locationModalOpen" title="Flyer location" @close="closeLocationPicker">
      <div class="grid gap-4 px-5 py-5">
        <form class="grid gap-3" @submit.prevent="applyTypedLocation">
          <label class="field-label">
            <span>Search location</span>
            <div class="location-search-field">
              <span class="location-search-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </span>
              <input
                v-model="locationSearch"
                class="field-input location-search-input"
                type="search"
                autocomplete="off"
                placeholder="Search city, area, or address"
                @input="handleLocationSearch"
              />
            </div>
          </label>

          <div class="overflow-hidden rounded-[1.25rem] border border-slate-200 bg-white">
            <button
              v-for="suggestion in locationSuggestions"
              :key="suggestion.id"
              class="location-suggestion"
              type="button"
              @click="selectLocationSuggestion(suggestion)"
            >
              <span class="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-blue-50 text-blue-700">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </span>
              <span class="min-w-0 flex-1">
                <span class="block truncate text-base font-medium text-slate-950">{{ suggestion.mainText }}</span>
                <span class="block truncate text-sm font-normal text-slate-500">{{ suggestion.secondaryText }}</span>
              </span>
            </button>

            <div v-if="locationLoading" class="px-4 py-5 text-center text-sm font-normal text-slate-500">
              Searching locations...
            </div>
            <div v-else-if="locationSearch.trim() && !locationSuggestions.length" class="px-4 py-5 text-center text-sm font-normal text-slate-500">
              No location suggestions found.
            </div>
          </div>

          <p class="m-0 text-sm font-normal text-slate-500">
            Selected: <span class="font-medium text-slate-950">{{ locationDraft || 'No location selected' }}</span>
          </p>

          <div v-if="locationError" class="rounded-2xl bg-amber-50 px-4 py-3 text-sm font-normal text-amber-700">
            {{ locationError }}
          </div>

          <div class="grid gap-4 sm:grid-cols-2">
            <button class="modal-secondary" type="button" @click="closeLocationPicker">Cancel</button>
            <button class="modal-primary" type="submit">Apply</button>
          </div>
        </form>
      </div>
    </BottomSheet>

    <BottomSheet v-if="reviewLocationModalOpen" title="Business search location" @close="closeReviewLocationPicker">
      <div class="grid gap-4 px-5 py-5">
        <form class="grid gap-3" @submit.prevent="applyReviewLocation">
          <label class="field-label">
            <span>Search location</span>
            <div class="location-search-field">
              <span class="location-search-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </span>
              <input
                v-model="reviewLocationSearch"
                class="field-input location-search-input"
                type="search"
                autocomplete="off"
                placeholder="Search city, area, or address"
                @input="handleReviewLocationSearch"
              />
            </div>
          </label>

          <div class="overflow-hidden rounded-[1.25rem] border border-slate-200 bg-white">
            <button
              v-for="suggestion in reviewLocationSuggestions"
              :key="suggestion.id"
              class="location-suggestion"
              type="button"
              @click="selectReviewLocationSuggestion(suggestion)"
            >
              <span class="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-blue-50 text-blue-700">
                <i class="ri-map-pin-2-line text-xl" aria-hidden="true"></i>
              </span>
              <span class="min-w-0 flex-1">
                <span class="block truncate text-base font-medium text-slate-950">{{ suggestion.mainText }}</span>
                <span class="block truncate text-sm font-normal text-slate-500">{{ suggestion.secondaryText }}</span>
              </span>
            </button>

            <div v-if="reviewLocationLoading" class="px-4 py-5 text-center text-sm font-normal text-slate-500">
              Searching locations...
            </div>
            <div v-else-if="reviewLocationSearch.trim() && !reviewLocationSuggestions.length" class="px-4 py-5 text-center text-sm font-normal text-slate-500">
              No location suggestions found.
            </div>
          </div>

          <p class="m-0 text-sm font-normal text-slate-500">
            Selected: <span class="font-medium text-slate-950">{{ reviewLocationDraft || 'No location selected' }}</span>
          </p>

          <div v-if="reviewLocationError" class="rounded-2xl bg-amber-50 px-4 py-3 text-sm font-normal text-amber-700">
            {{ reviewLocationError }}
          </div>

          <div class="grid gap-4 sm:grid-cols-2">
            <button class="modal-secondary" type="button" @click="closeReviewLocationPicker">Cancel</button>
            <button class="modal-primary" type="submit">Apply</button>
          </div>
        </form>
      </div>
    </BottomSheet>

    <BottomSheet v-if="visibilityModalOpen" title="Post Visibility" @close="visibilityModalOpen = false">
      <div class="grid gap-5 px-5 py-5">
        <div>
          <h3 class="m-0 text-lg font-medium text-slate-950">Who can see your post?</h3>
          <p class="m-0 mt-3 text-base leading-6 text-slate-500">Your default audience is set to Public, but you can change the audience of this specific post.</p>
        </div>
        <button
          v-for="option in visibilityOptions"
          :key="option.label"
          class="flex items-center gap-4 text-left"
          type="button"
          @click="form.visibility = option.label"
        >
          <span class="grid h-10 w-10 place-items-center rounded-full bg-blue-50 text-blue-700" v-html="option.icon"></span>
          <span class="min-w-0 flex-1">
            <span class="block text-lg font-medium text-slate-950">{{ option.label }}</span>
            <span class="block text-base text-slate-400">{{ option.description }}</span>
          </span>
          <span class="grid h-8 w-8 place-items-center rounded-full border-2" :class="form.visibility === option.label ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 text-transparent'">✓</span>
        </button>
      </div>
      <div class="grid gap-4 px-5 pb-5 sm:grid-cols-2">
        <button class="modal-secondary" type="button" @click="visibilityModalOpen = false">Cancel</button>
        <button class="modal-primary" type="button" @click="visibilityModalOpen = false">Apply</button>
      </div>
    </BottomSheet>

    <BottomSheet v-if="boardModalOpen" title="Save to board" @close="boardModalOpen = false">
      <div v-if="!creatingBoard" class="grid gap-1.5 px-3 py-2.5">
              <button class="modal-primary compact-modal-action" type="button" @click="showCreateBoard">
                + Create Board
              </button>
        <div v-if="boardsLoading" class="px-4 py-4 text-center text-sm font-normal text-slate-500">
          Loading boards...
        </div>
        <div v-else-if="!boards.length" class="px-4 py-4 text-center text-sm font-normal text-slate-500">
          No boards found.
        </div>
        <button
          v-for="board in boards"
          :key="board.id"
          class="modal-list-row"
          type="button"
          @click="selectBoard(board)"
        >
            <span class="min-w-0">
            <span class="block truncate">{{ board.name }}</span>
            <span v-if="board.description" class="mt-0.5 block truncate text-xs font-medium text-slate-500">{{ board.description }}</span>
          </span>
          <span class="grid h-6 w-6 shrink-0 place-items-center rounded border-2 border-blue-600 text-blue-600">{{ form.boardId === board.id ? '✓' : '' }}</span>
        </button>
      </div>
      <form v-else class="grid gap-2.5 px-3 py-2.5" @submit.prevent="saveBoard">
        <label class="field-label">
          <span>Board name</span>
          <input
            v-model="newBoardName"
            class="field-input"
            type="text"
            placeholder="Enter board name"
            autocomplete="off"
            autofocus
          />
        </label>
        <div v-if="boardError" class="rounded-xl bg-red-50 px-3 py-2 text-sm font-normal text-red-700">
          {{ boardError }}
        </div>
        <div class="grid gap-2 sm:grid-cols-2">
          <button class="modal-secondary" type="button" :disabled="boardSaving" @click="cancelCreateBoard">Cancel</button>
          <button class="modal-primary" type="submit" :disabled="boardSaving">
            {{ boardSaving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </form>
    </BottomSheet>

    <BottomSheet v-if="contentTypeModalOpen" title="Content Type" save-label="Save" @save="contentTypeModalOpen = false" @close="contentTypeModalOpen = false">
      <div v-if="contentTypesLoading" class="px-5 py-8 text-center text-sm font-normal text-slate-500">
        Loading content types...
      </div>
      <div v-else-if="!contentTypes.length" class="px-5 py-8 text-center text-sm font-normal text-slate-500">
        No content types found.
      </div>
      <div v-else class="divide-y divide-slate-200">
        <button
          v-for="type in contentTypes"
          :key="type.id"
          class="modal-list-row"
          type="button"
          @click="selectContentType(type)"
        >
          <span class="flex min-w-0 items-center gap-4">
            <span class="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-blue-50 text-blue-700">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                <circle cx="12" cy="8" r="3" />
                <path d="M5 21a7 7 0 0 1 14 0" />
              </svg>
            </span>
            <span class="truncate text-base font-medium">{{ type.name }}</span>
          </span>
          <span class="grid h-6 w-6 place-items-center rounded border-2 border-blue-600 text-blue-600">{{ form.contentTypeId === type.id ? '✓' : '' }}</span>
        </button>
      </div>
    </BottomSheet>
  </section>
</template>

<script setup>
import { computed, defineComponent, h, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import flyerPlaceholder from '@/assets/flyer-placeholder.png'
import RichTextEditor from '@/components/common/RichTextEditor.vue'
import api from '@/services/api'
import { formatDayMonthYear } from '@/utils/date'
import { loadGoogleMapsAPI } from '@/utils/googleMaps'
import { useSessionStore } from '@/stores/session'

const props = defineProps({
  initialFlyer: { type: Object, default: null },
  flyerId: { type: [String, Number], default: '' },
  pageTitle: { type: String, default: 'Add New Flyer' },
  submitLabel: { type: String, default: 'Post' },
  redirectTo: { type: String, default: '/explore' }
})

const router = useRouter()
const session = useSessionStore()
const addPhotosInput = ref(null)
const currentSlide = ref(0)
const creationMode = ref('')
const checkinOption = ref({
  title: 'Checkins',
  description: 'They primarily apply to travel'
})
const businesses = ref([])
const businessesLoading = ref(false)
const businessesError = ref('')
const businessSearch = ref('')
const businessSearchLoading = ref(false)
const businessSearchError = ref('')
const selectedBusiness = ref(null)
const isReviewSubmitting = ref(false)
const reviewSubmitError = ref('')
const countriesModalOpen = ref(false)
const dateModalOpen = ref(false)
const visibilityModalOpen = ref(false)
const boardModalOpen = ref(false)
const contentTypeModalOpen = ref(false)
const locationModalOpen = ref(false)
const reviewLocationModalOpen = ref(false)
const countryDraft = ref([])
const boards = ref([])
const boardsLoading = ref(false)
const creatingBoard = ref(false)
const boardSaving = ref(false)
const newBoardName = ref('')
const boardError = ref('')
const contentTypes = ref([])
const contentTypesLoading = ref(false)
const locationSearch = ref('')
const locationDraft = ref('')
const locationSuggestions = ref([])
const locationLoading = ref(false)
const locationError = ref('')
const reviewBusinessLocation = ref('')
const reviewBusinessLocationCoords = ref(null)
const reviewLocationSearch = ref('')
const reviewLocationDraft = ref('')
const reviewLocationSuggestions = ref([])
const reviewLocationLoading = ref(false)
const reviewLocationError = ref('')
const selectedReviewLocationCoords = ref(null)
const selectedReviewLocationSuggestion = ref(null)
const selectedLocationCoords = ref(null)
const selectedLocationParts = ref({})
const flyerLocationCoords = ref(null)
const flyerLocationParts = ref({})
const isSubmitting = ref(false)
const submitError = ref('')
let autocompleteService = null
let geocoder = null
let locationSearchTimer = null
let reviewLocationSearchTimer = null
let businessSearchTimer = null
let businessRequestId = 0

const countries = ['India', 'United States']

const form = reactive({
  description: '',
  flyerLocation: '',
  audienceMode: 'Nationwide',
  audienceSameAsFlyer: false,
  countries: [],
  startsDate: '',
  endsDate: '',
  visibility: 'Public',
  categoryId: '',
  boardId: '',
  board: '',
  contentTypeId: '',
  contentType: ''
})

const errors = reactive({
  description: '',
  flyerLocation: '',
  audience: '',
  dates: '',
  board: '',
  contentType: ''
})

const images = ref([])

const reviewForm = reactive({
  rating: 0
})

const reviewErrors = reactive({
  rating: '',
  review: ''
})

const toDateInputValue = (date = new Date()) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const todayDate = computed(() => toDateInputValue())
const endDateMin = computed(() => form.startsDate || todayDate.value)

const clearError = (field) => {
  if (field && Object.prototype.hasOwnProperty.call(errors, field)) {
    errors[field] = ''
  }
}

const decodeHtmlEntities = (value = '') => {
  let decoded = String(value || '')
  if (!decoded) return ''

  for (let index = 0; index < 5; index += 1) {
    const previous = decoded
    if (typeof document !== 'undefined') {
      const textarea = document.createElement('textarea')
      textarea.innerHTML = decoded
      decoded = textarea.value
    } else {
      decoded = decoded
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
    }
    if (decoded === previous) break
  }

  return decoded
}

const normalizedDescription = () => decodeHtmlEntities(form.description || '').trim()

const descriptionText = () => normalizedDescription().replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim()

const hasOwnValue = (source, key) => (
  source &&
  Object.prototype.hasOwnProperty.call(source, key) &&
  source[key] !== undefined &&
  source[key] !== null
)

const getInitialFlyerLocation = (flyer = {}) => {
  if (hasOwnValue(flyer, 'flyerLocation')) return flyer.flyerLocation || ''
  if (hasOwnValue(flyer, 'flyer_location')) return flyer.flyer_location || ''
  return flyer.location || flyer.address || ''
}

const validateField = (field) => {
  if (field === 'description') {
    errors.description = descriptionText() || images.value.length ? '' : 'Add a description or at least one image.'
  }

  if (field === 'flyerLocation') {
    errors.flyerLocation = form.audienceSameAsFlyer && !form.flyerLocation.trim()
      ? 'Choose a flyer location when audience is same as flyer.'
      : ''
  }

  if (field === 'audience') {
    errors.audience = form.audienceSameAsFlyer || form.countries.length
      ? ''
      : 'Choose who should see this flyer.'
  }

  if (field === 'dates') {
    errors.dates = ''
    if (form.startsDate && form.startsDate < todayDate.value) {
      errors.dates = 'Start date cannot be in the past.'
    } else if (form.endsDate && form.endsDate < todayDate.value) {
      errors.dates = 'End date cannot be in the past.'
    } else if (form.startsDate && form.endsDate && form.endsDate < form.startsDate) {
      errors.dates = 'End date must be after start date.'
    }
  }

  if (field === 'contentType') {
    errors.contentType = form.contentTypeId ? '' : 'Select a content type.'
  }

  return !errors[field]
}

const validateForm = () => {
  const fields = ['description', 'flyerLocation', 'audience', 'dates', 'contentType']
  fields.forEach(validateField)
  return fields.every((field) => !errors[field])
}

const hydrateInitialFlyer = () => {
  const flyer = props.initialFlyer
  if (!flyer) return

  const flyerType = String(flyer.flyer_type || flyer.flyerType || '').toLowerCase()
  const initialFlyerType = flyerType === 'review' || flyerType === 'checkin'
    ? flyerType
    : 'flyer'
  creationMode.value = initialFlyerType

  if (initialFlyerType === 'review' || initialFlyerType === 'checkin') {
    const businessName = firstFilled(flyer.business_name, flyer.businessName, flyer.s_business, flyer.title)
    const businessAddress = firstFilled(flyer.business_adress, flyer.business_address, flyer.businessAddress, flyer.s_address, flyer.location, flyer.address)
    const businessLatitude = firstFilled(flyer.business_lat, flyer.businessLatitude, flyer.business_latitude, flyer.s_latit, flyer.lat, flyer.latitude)
    const businessLongitude = firstFilled(flyer.business_long, flyer.businessLong, flyer.business_longitude, flyer.s_longit, flyer.long, flyer.lng, flyer.longitude)

    selectedBusiness.value = businessName
      ? {
          id: String(flyer.business_id || flyer.businessId || ''),
          name: String(businessName),
          address: String(businessAddress || ''),
          meta: String(businessAddress || ''),
          image: String(flyer.business_image || flyer.businessImage || ''),
          businessLat: businessLatitude || '',
          businessLong: businessLongitude || '',
          coords: businessLatitude !== '' && businessLongitude !== ''
            ? { lat: businessLatitude, lng: businessLongitude }
            : null,
          parts: parseLocationTextParts(businessAddress),
          raw: flyer
        }
      : null
    reviewForm.rating = initialFlyerType === 'review'
      ? Number(flyer.review_count || flyer.rating || 0)
      : 0
  } else {
    selectedBusiness.value = null
    reviewForm.rating = 0
  }

  form.description = decodeHtmlEntities(
    initialFlyerType === 'review' || initialFlyerType === 'checkin'
      ? flyer.review_comment || flyer.review || flyer.description || ''
      : flyer.description || flyer.desc || flyer.caption || ''
  ).trim()
  form.flyerLocation = getInitialFlyerLocation(flyer)
  form.startsDate = flyer.startsDate || flyer.start_date || flyer.offer_start_date || ''
  form.endsDate = flyer.endsDate || flyer.end_date || flyer.offer_end_date || ''
  form.visibility = flyer.visibility || 'Public'
  const targetCountry = flyer.country_id || flyer.country || flyer.target_country || ''
  const selectedCountries = String(targetCountry || '').split(',').map((country) => country.trim()).filter(Boolean)
  form.audienceSameAsFlyer = selectedCountries.some((country) => country.toLowerCase() === 'same as flyer')
  form.countries = selectedCountries.filter((country) => country.toLowerCase() !== 'same as flyer')
  countryDraft.value = [...form.countries]
  form.audienceMode = targetCountry ? 'Nationwide' : form.audienceMode
  const initialLatitude = firstFilled(flyer.latitude, flyer.lat, flyer.flyer_latitude, flyer.location_latitude)
  const initialLongitude = firstFilled(flyer.longitude, flyer.lng, flyer.long, flyer.flyer_longitude, flyer.location_longitude)
  flyerLocationCoords.value = initialLatitude && initialLongitude
    ? { lat: Number(initialLatitude), lng: Number(initialLongitude) }
    : null
  flyerLocationParts.value = mergeLocationParts(
    {
      state: firstFilled(flyer.state, flyer.flyer_state, flyer.location_state),
      city: firstFilled(flyer.city, flyer.flyer_city, flyer.location_city),
      area: firstFilled(flyer.area, flyer.flyer_area, flyer.location_area),
      locality: firstFilled(flyer.locality, flyer.flyer_locality, flyer.location_locality),
      street: firstFilled(flyer.street, flyer.flyer_street, flyer.location_street),
      country_id: firstFilled(flyer.country_id, flyer.country, flyer.target_country, flyer.flyer_country, flyer.location_country)
    },
    parseLocationTextParts(form.flyerLocation)
  )
  form.categoryId = String(flyer.categoryId || flyer.category_id || flyer.category || '')
  form.boardId = String(flyer.boardId || flyer.board_id || '')
  form.board = flyer.board || ''
  form.contentTypeId = String(flyer.contentTypeId || flyer.content_type_id || flyer.category_id || '')
  form.contentType = flyer.contentType || flyer.content_type || flyer.category_name || flyer.category || ''

  const imageList = Array.isArray(flyer.images)
    ? flyer.images
    : [flyer.image || flyer.flyer_image || flyer.image_url].filter(Boolean)
  const hydratedImageSlots = Array.isArray(flyer.imageSlots) && flyer.imageSlots.length
    ? flyer.imageSlots
        .filter((image) => image?.url && Number.isInteger(image.slotIndex) && image.slotIndex >= 0 && image.slotIndex < 5)
        .map((image) => ({ src: image.url, slotIndex: image.slotIndex }))
    : Array.from(new Set(imageList)).slice(0, 5).map((image, slotIndex) => ({ src: image, slotIndex }))

  images.value = hydratedImageSlots.map((image) => ({
    id: `existing-${image.slotIndex}-${image.src}`,
    src: image.src,
    remoteUrl: image.src,
    slotIndex: image.slotIndex
  }))
  currentSlide.value = 0
}

const activeImage = computed(() => images.value[currentSlide.value] || images.value[0])
const dateSummary = computed(() => {
  const start = formatDayMonthYear(form.startsDate)
  const end = formatDayMonthYear(form.endsDate)
  if (start && end) return `${start} - ${end}`
  return start || end || 'Starts date and ends date'
})
const visibilityDescription = computed(() => visibilityOptions.find((item) => item.label === form.visibility)?.description || '')
const updateFlyerId = computed(() => String(props.flyerId || props.initialFlyer?.id || props.initialFlyer?.flyer_id || '').trim())
const isUpdateMode = computed(() => Boolean(updateFlyerId.value))
const isFlyerMode = computed(() => creationMode.value === 'flyer' || (isUpdateMode.value && !creationMode.value))
const isBusinessPostMode = computed(() => ['review', 'checkin'].includes(creationMode.value))
const displayPageTitle = computed(() => {
  if (isUpdateMode.value) return props.pageTitle
  if (!creationMode.value) return 'Create'
  if (isBusinessPostMode.value && !selectedBusiness.value) return 'Select Business'
  if (creationMode.value === 'review') return 'Write a Review'
  if (creationMode.value === 'checkin') return 'Write a Checkin'
  return props.pageTitle
})
const filteredBusinesses = computed(() => businesses.value)
const visibleBusinessError = computed(() => (
  businessSearch.value.trim() ? businessSearchError.value : businessesError.value
))

const extractBusinessRows = (payload) => {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.list,
    payload.data,
    payload.result,
    payload.businesses,
    payload.vendors,
    payload.data?.list,
    payload.data?.data,
    payload.data?.result,
    payload.data?.businesses,
    payload.data?.vendors
  ]
  return candidates.find(Array.isArray) || []
}

const normalizeBusiness = (business, index) => {
  const name = String(business?.business_name ?? business?.s_business ?? business?.name ?? business?.title ?? business?.s_name ?? '').trim()
  const category = String(business?.businesscategory ?? business?.category_name ?? business?.s_type ?? '').trim()
  const address = String(business?.business_adress ?? business?.business_address ?? business?.formatted_address ?? business?.s_address ?? business?.address ?? business?.vicinity ?? business?.city ?? '').trim()
  const distance = String(business?.distance ?? '').trim()
  const latitude = business?.business_lat ?? business?.s_latit ?? business?.lat ?? business?.latitude ?? business?.geometry?.location?.lat ?? ''
  const longitude = business?.business_long ?? business?.s_longit ?? business?.long ?? business?.lng ?? business?.longitude ?? business?.geometry?.location?.lng ?? ''
  const parsedParts = parseLocationTextParts(address)

  return {
    id: String(business?.business_id ?? business?.place_id ?? business?.s_id ?? business?.id ?? business?.user_id ?? index + 1),
    placeId: String(business?.place_id ?? ''),
    name,
    image: String(business?.s_user_img ?? business?.business_image ?? business?.image ?? business?.logo ?? '').trim(),
    meta: [category, address, distance].filter(Boolean).join(' · '),
    address,
    businessLat: latitude,
    businessLong: longitude,
    coords: latitude !== '' && longitude !== '' ? { lat: latitude, lng: longitude } : null,
    parts: {
      ...parsedParts,
      state: business?.state ?? parsedParts.state,
      city: business?.city ?? parsedParts.city,
      area: business?.area ?? parsedParts.area,
      locality: business?.locality ?? parsedParts.locality,
      street: business?.street ?? parsedParts.street,
      country_id: business?.country_id ?? business?.country ?? parsedParts.country_id
    },
    initial: name.charAt(0).toUpperCase() || 'B',
    raw: business
  }
}

const fetchBusinesses = async (key = businessSearch.value.trim()) => {
  const requestId = ++businessRequestId
  const isSearch = Boolean(key)
  businessesLoading.value = !isSearch
  businessSearchLoading.value = isSearch
  if (isSearch) businessSearchError.value = ''
  else businessesError.value = ''

  try {
    const reviewCoords = reviewBusinessLocationCoords.value || {}
    const { data } = await api.getBusinessList({
      key,
      lat: reviewCoords.lat !== undefined ? String(reviewCoords.lat) : '',
      long: reviewCoords.lng !== undefined ? String(reviewCoords.lng) : ''
    })
    if (requestId !== businessRequestId) return
    businesses.value = extractBusinessRows(data)
      .map(normalizeBusiness)
      .filter((business) => business.name)
  } catch (error) {
    if (requestId !== businessRequestId) return
    businesses.value = []
    const message = error?.response?.data?.message || error?.message || 'Businesses could not be loaded. Please try again.'
    if (isSearch) businessSearchError.value = message
    else businessesError.value = message
  } finally {
    if (requestId === businessRequestId) {
      businessesLoading.value = false
      businessSearchLoading.value = false
    }
  }
}

const handleBusinessSearch = () => {
  businessSearchError.value = ''
  if (businessSearchTimer) clearTimeout(businessSearchTimer)

  const query = businessSearch.value.trim()
  businessSearchLoading.value = true
  businessSearchTimer = setTimeout(() => fetchBusinesses(query), 300)
}

const clearBusinessSearch = () => {
  businessSearch.value = ''
  businessSearchError.value = ''
  businessSearchLoading.value = false
  if (businessSearchTimer) {
    clearTimeout(businessSearchTimer)
    businessSearchTimer = null
  }
  fetchBusinesses('')
}

const retryBusinessLoad = () => {
  if (businessSearch.value.trim()) {
    fetchBusinesses(businessSearch.value.trim())
    return
  }
  fetchBusinesses('')
}

const selectBusiness = (business) => {
  selectedBusiness.value = business
  reviewSubmitError.value = ''
}

const changeBusiness = () => {
  selectedBusiness.value = null
  clearBusinessSearch()
  reviewSubmitError.value = ''
}

const handleCreateBack = () => {
  if (isUpdateMode.value) {
    router.back()
    return
  }

  if (isBusinessPostMode.value && selectedBusiness.value) {
    changeBusiness()
    return
  }

  if (creationMode.value) {
    creationMode.value = ''
    submitError.value = ''
    reviewSubmitError.value = ''
    return
  }

  router.back()
}

const extractContentTypeItems = (payload) => {
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.list,
    payload.data,
    payload.result,
    payload.content_types,
    payload.data?.list,
    payload.data?.data,
    payload.data?.result,
    payload.data?.content_types
  ]

  const source = candidates.find(Array.isArray) || []
  return source
    .map((item, index) => ({
      id: String(item?.id ?? item?.content_type_id ?? item?.value ?? index + 1),
      name: String(item?.name ?? item?.label ?? item?.title ?? '').trim(),
      categoryId: String(item?.category_id ?? item?.category ?? item?.cat_id ?? '').trim()
    }))
    .filter((item) => item.name)
}

const extractCheckinOption = (payload) => {
  const fallback = { title: 'Checkins', description: 'They primarily apply to travel' }
  if (!payload || typeof payload !== 'object') return fallback

  const candidates = [
    payload.data,
    payload.list,
    payload.result,
    payload.checkins,
    payload.data?.data,
    payload.data?.list,
    payload.data?.result,
    payload.data?.checkins
  ]
  const source = candidates.find((value) => Array.isArray(value) && value.length)?.[0]
    || candidates.find((value) => value && typeof value === 'object' && !Array.isArray(value))
    || payload

  return {
    title: String(source?.title ?? source?.name ?? source?.label ?? source?.checkin_name ?? fallback.title).trim() || fallback.title,
    description: String(source?.description ?? source?.subtitle ?? source?.help_text ?? source?.checkin_description ?? fallback.description).trim() || fallback.description
  }
}

const fetchCheckinOption = async () => {
  try {
    const { data } = await api.listCheckins({
      user_id: session.userId || session.user?.user_id || ''
    })
    checkinOption.value = extractCheckinOption(data)
  } catch {
    checkinOption.value = {
      title: 'Checkins',
      description: 'They primarily apply to travel'
    }
  }
}

const extractBoardItems = (payload) => {
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.list,
    payload.data,
    payload.result,
    payload.boards,
    payload.data?.list,
    payload.data?.data,
    payload.data?.result,
    payload.data?.boards
  ]

  const source = candidates.find(Array.isArray) || []
  return source
    .map((item, index) => ({
      id: String(item?.id ?? item?.board_id ?? item?.value ?? index + 1),
      name: String(item?.name ?? item?.board_name ?? item?.title ?? item?.label ?? '').trim(),
      description: String(item?.description ?? item?.desc ?? item?.subtitle ?? '').trim()
    }))
    .filter((item) => item.name)
}

const fallbackLocationSuggestions = [
  { id: 'country-us', mainText: 'United States', secondaryText: 'Country', description: 'United States' },
  { id: 'country-in', mainText: 'India', secondaryText: 'Country', description: 'India' },
  { id: 'city-dallas', mainText: 'Dallas', secondaryText: 'Texas, United States', description: 'Dallas, TX, United States' },
  { id: 'city-austin', mainText: 'Austin', secondaryText: 'Texas, United States', description: 'Austin, TX, United States' },
  { id: 'city-hyderabad', mainText: 'Hyderabad', secondaryText: 'Telangana, India', description: 'Hyderabad, Telangana, India' }
]

const emptyLocationParts = () => ({
  state: '',
  city: '',
  area: '',
  locality: '',
  street: '',
  country_id: ''
})

const firstFilled = (...values) => values.find((value) => String(value ?? '').trim() !== '') || ''

const mergeLocationParts = (...sources) =>
  sources.reduce((merged, source = {}) => {
    Object.keys(merged).forEach((key) => {
      const value = source?.[key]
      if (!String(merged[key] ?? '').trim() && String(value ?? '').trim()) {
        merged[key] = value
      }
    })
    return merged
  }, emptyLocationParts())

const parseAddressComponents = (components = []) => {
  const parts = emptyLocationParts()
  components.forEach((component = {}) => {
    const types = component.types || []
    const longName = component.long_name || ''

    if (types.includes('country')) parts.country_id = longName
    if (types.includes('administrative_area_level_1')) parts.state = longName
    if (types.includes('locality')) parts.city = longName
    if (!parts.city && types.includes('postal_town')) parts.city = longName
    if (!parts.city && types.includes('administrative_area_level_2')) parts.city = longName
    if (types.includes('sublocality') || types.includes('sublocality_level_1') || types.includes('neighborhood')) parts.locality = longName
    if (types.includes('route')) parts.street = longName
  })
  parts.area = parts.locality || parts.city || ''
  return parts
}

const parseLocationTextParts = (value = '') => {
  const pieces = String(value).split(',').map((part) => part.trim()).filter(Boolean)
  if (!pieces.length) return emptyLocationParts()

  const parts = emptyLocationParts()
  parts.street = pieces.length > 3 ? pieces[0] : ''
  parts.city = pieces.length > 3 ? pieces[pieces.length - 3] : pieces[0] || ''
  parts.state = pieces.length > 2 ? pieces[pieces.length - 2] : ''
  parts.country_id = pieces[pieces.length - 1] || ''
  parts.locality = pieces.length > 3 ? pieces[1] : ''
  parts.area = parts.locality || parts.city
  return parts
}

const visibilityOptions = [
  {
    label: 'Public',
    description: 'Anyone on Netflyr can see this flyer',
    icon: '<svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></svg>'
  },
  {
    label: 'Only My Followers',
    description: 'Visible to people who follows you',
    icon: '<svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>'
  },
  {
    label: 'Only me',
    description: 'Flyer private for Yourself',
    icon: '<svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>'
  }
]

const openAddPhotos = () => {
  addPhotosInput.value?.click()
}

const getAvailableImageSlots = (requestedCount) => {
  const occupiedSlots = new Set(
    images.value
      .map((image) => image.slotIndex)
      .filter((slotIndex) => Number.isInteger(slotIndex))
  )
  const lastOccupiedSlot = occupiedSlots.size ? Math.max(...occupiedSlots) : -1
  const availableSlots = imageFieldNames
    .map((field, slotIndex) => slotIndex)
    .filter((slotIndex) => !occupiedSlots.has(slotIndex))
  const trailingSlots = availableSlots.filter((slotIndex) => slotIndex > lastOccupiedSlot)
  const gapSlots = availableSlots.filter((slotIndex) => slotIndex <= lastOccupiedSlot)
  const imageCount = Math.min(requestedCount, availableSlots.length)

  // Keep newly selected images after the current preview whenever a trailing
  // server slot exists. When gaps also have to be reused, take the latest gaps
  // first so the selected files retain their order and disturb the preview as
  // little as possible.
  const requiredGapCount = Math.max(0, imageCount - trailingSlots.length)
  return [
    ...(requiredGapCount ? gapSlots.slice(-requiredGapCount) : []),
    ...trailingSlots.slice(0, imageCount - requiredGapCount)
  ]
}

const addPhotos = (event) => {
  const files = Array.from(event.target.files || []).slice(0, Math.max(0, 5 - images.value.length))
  const availableSlots = getAvailableImageSlots(files.length)
  const addedImages = files.map((file, index) => ({
    id: `${Date.now()}-${index}-${Math.random()}`,
    src: URL.createObjectURL(file),
    file,
    slotIndex: availableSlots[index]
  }))

  images.value = [...images.value, ...addedImages]
    .sort((first, second) => first.slotIndex - second.slotIndex)
  if (addedImages.length) {
    currentSlide.value = images.value.findIndex((image) => image.id === addedImages[0].id)
  }
  event.target.value = ''
}

const removeImage = (index) => {
  const removedImage = images.value[index]
  if (removedImage?.file && removedImage.src?.startsWith('blob:')) {
    URL.revokeObjectURL(removedImage.src)
  }
  images.value.splice(index, 1)
  currentSlide.value = Math.min(currentSlide.value, Math.max(images.value.length - 1, 0))
}

const applyCountries = () => {
  form.countries = [...countryDraft.value]
  form.audienceMode = 'Nationwide'
  validateField('audience')
  if (form.audienceSameAsFlyer) validateField('flyerLocation')
  else clearError('flyerLocation')
  countriesModalOpen.value = false
}

const clearDateRange = () => {
  form.startsDate = ''
  form.endsDate = ''
  validateField('dates')
}

const syncEndDateFromStart = () => {
  if (!form.startsDate) return
  if (!form.endsDate || form.endsDate < form.startsDate) {
    form.endsDate = form.startsDate
  }
  validateField('dates')
}

const selectSameAsFlyer = () => {
  form.audienceSameAsFlyer = !form.audienceSameAsFlyer
  clearError('audience')
  if (form.audienceSameAsFlyer) validateField('flyerLocation')
  else clearError('flyerLocation')
}

const toggleAudienceCountry = (country) => {
  form.audienceMode = 'Nationwide'
  form.countries = form.countries.includes(country)
    ? form.countries.filter((item) => item !== country)
    : [...form.countries, country]
  countryDraft.value = [...form.countries]
  clearError('audience')
  if (form.audienceSameAsFlyer) validateField('flyerLocation')
  else clearError('flyerLocation')
}

const openLocationPicker = async () => {
  locationDraft.value = form.flyerLocation || ''
  locationSearch.value = form.flyerLocation || ''
  locationSuggestions.value = []
  selectedLocationCoords.value = flyerLocationCoords.value
  selectedLocationParts.value = { ...emptyLocationParts(), ...flyerLocationParts.value }
  locationError.value = ''
  locationModalOpen.value = true

  try {
    await loadGoogleMapsAPI()
    autocompleteService = new window.google.maps.places.AutocompleteService()
    geocoder = new window.google.maps.Geocoder()
    if (locationSearch.value.trim().length >= 2) {
      handleLocationSearch()
    }
  } catch {
    autocompleteService = null
    geocoder = null
    locationError.value = 'Google location search is not available here. You can still type and apply a location.'
  }
}

const closeLocationPicker = () => {
  locationModalOpen.value = false
  locationLoading.value = false
  locationError.value = ''
  locationSuggestions.value = []
  if (locationSearchTimer) {
    clearTimeout(locationSearchTimer)
    locationSearchTimer = null
  }
}

const openReviewLocationPicker = async () => {
  reviewLocationDraft.value = reviewBusinessLocation.value
  reviewLocationSearch.value = reviewBusinessLocation.value
  reviewLocationSuggestions.value = []
  selectedReviewLocationCoords.value = reviewBusinessLocationCoords.value
  reviewLocationError.value = ''
  reviewLocationModalOpen.value = true

  try {
    await loadGoogleMapsAPI()
    autocompleteService = new window.google.maps.places.AutocompleteService()
    geocoder = new window.google.maps.Geocoder()
    if (reviewLocationSearch.value.trim().length >= 2) handleReviewLocationSearch()
  } catch {
    autocompleteService = null
    geocoder = null
    reviewLocationError.value = 'Google location search is not available here. You can still type and apply a location.'
  }
}

const closeReviewLocationPicker = () => {
  reviewLocationModalOpen.value = false
  reviewLocationLoading.value = false
  reviewLocationError.value = ''
  reviewLocationSuggestions.value = []
  if (reviewLocationSearchTimer) {
    clearTimeout(reviewLocationSearchTimer)
    reviewLocationSearchTimer = null
  }
}

const normalizePrediction = (prediction, index) => ({
  id: prediction.place_id || `prediction-${index}`,
  placeId: prediction.place_id || '',
  mainText: prediction.structured_formatting?.main_text || prediction.description,
  secondaryText: prediction.structured_formatting?.secondary_text || '',
  description: prediction.description || prediction.structured_formatting?.main_text || ''
})

const resolveLocationDetail = (request) => new Promise((resolve) => {
  if (!geocoder || !request) {
    resolve(null)
    return
  }

  geocoder.geocode(request, (results, status) => {
    if (status === window.google.maps.GeocoderStatus.OK && results?.[0]?.geometry?.location) {
      const result = results[0]
      const location = results[0].geometry.location
      resolve({
        coords: { lat: location.lat(), lng: location.lng() },
        parts: parseAddressComponents(result.address_components || [])
      })
      return
    }

    resolve(null)
  })
})

const fetchLocationSuggestions = async () => {
  const query = locationSearch.value.trim()
  locationError.value = ''

  if (!query) {
    locationSuggestions.value = []
    locationLoading.value = false
    return
  }

  if (!autocompleteService) {
    const lowerQuery = query.toLowerCase()
    locationSuggestions.value = fallbackLocationSuggestions.filter((item) =>
      `${item.mainText} ${item.secondaryText} ${item.description}`.toLowerCase().includes(lowerQuery)
    )
    locationLoading.value = false
    return
  }

  locationLoading.value = true
  autocompleteService.getPlacePredictions(
    {
      input: query,
      types: ['geocode']
    },
    (predictions, status) => {
      locationLoading.value = false
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
        locationSuggestions.value = predictions.map(normalizePrediction)
        return
      }

      locationSuggestions.value = []
      if (status && status !== window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        locationError.value = 'Location suggestions could not be loaded. You can still apply the typed location.'
      }
    }
  )
}

const fetchReviewLocationSuggestions = async () => {
  const query = reviewLocationSearch.value.trim()
  reviewLocationError.value = ''

  if (!query) {
    reviewLocationSuggestions.value = []
    reviewLocationLoading.value = false
    return
  }

  if (!autocompleteService) {
    const lowerQuery = query.toLowerCase()
    reviewLocationSuggestions.value = fallbackLocationSuggestions.filter((item) =>
      `${item.mainText} ${item.secondaryText} ${item.description}`.toLowerCase().includes(lowerQuery)
    )
    reviewLocationLoading.value = false
    return
  }

  autocompleteService.getPlacePredictions(
    { input: query },
    (predictions, status) => {
      reviewLocationLoading.value = false
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
        reviewLocationSuggestions.value = predictions.map(normalizePrediction)
        return
      }

      reviewLocationSuggestions.value = []
      if (status && status !== window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        reviewLocationError.value = 'Location suggestions could not be loaded. You can still apply the typed location.'
      }
    }
  )
}

const handleLocationSearch = () => {
  locationDraft.value = locationSearch.value.trim()
  selectedLocationCoords.value = null
  selectedLocationParts.value = emptyLocationParts()
  locationLoading.value = Boolean(locationSearch.value.trim())

  if (locationSearchTimer) clearTimeout(locationSearchTimer)
  locationSearchTimer = setTimeout(fetchLocationSuggestions, 250)
}

const handleReviewLocationSearch = () => {
  reviewLocationDraft.value = reviewLocationSearch.value.trim()
  selectedReviewLocationCoords.value = null
  selectedReviewLocationSuggestion.value = null
  reviewLocationLoading.value = Boolean(reviewLocationSearch.value.trim())

  if (reviewLocationSearchTimer) clearTimeout(reviewLocationSearchTimer)
  reviewLocationSearchTimer = setTimeout(fetchReviewLocationSuggestions, 250)
}

const selectLocationSuggestion = async (suggestion) => {
  locationDraft.value = suggestion.description
  locationSearch.value = suggestion.description
  locationSuggestions.value = []
  const detail = suggestion.placeId
    ? await resolveLocationDetail({ placeId: suggestion.placeId })
    : await resolveLocationDetail({ address: suggestion.description })
  selectedLocationCoords.value = detail?.coords || null
  selectedLocationParts.value = detail?.parts || parseLocationTextParts(suggestion.description)
}

const selectReviewLocationSuggestion = async (suggestion) => {
  selectedReviewLocationSuggestion.value = suggestion
  reviewLocationDraft.value = suggestion.description
  reviewLocationSearch.value = suggestion.description
  reviewLocationSuggestions.value = []
  const detail = suggestion.placeId
    ? await resolveLocationDetail({ placeId: suggestion.placeId })
    : await resolveLocationDetail({ address: suggestion.description })
  selectedReviewLocationCoords.value = detail?.coords || null
}

const applyTypedLocation = async () => {
  const nextLocation = locationDraft.value || locationSearch.value.trim()
  if (!nextLocation) return

  const detail = selectedLocationCoords.value
    ? { coords: selectedLocationCoords.value, parts: selectedLocationParts.value }
    : await resolveLocationDetail({ address: nextLocation })
  const coords = detail?.coords || null
  const parts = detail?.parts || parseLocationTextParts(nextLocation)
  flyerLocationCoords.value = coords
  flyerLocationParts.value = { ...emptyLocationParts(), ...parts }
  form.flyerLocation = nextLocation
  clearError('flyerLocation')
  closeLocationPicker()
}

const applyReviewLocation = async () => {
  const nextLocation = reviewLocationDraft.value || reviewLocationSearch.value.trim()
  if (!nextLocation) return

  const detail = selectedReviewLocationCoords.value
    ? { coords: selectedReviewLocationCoords.value }
    : await resolveLocationDetail({ address: nextLocation })

  const coords = detail?.coords || null
  const suggestion = selectedReviewLocationSuggestion.value
  const businessName = suggestion?.mainText || nextLocation.split(',')[0]?.trim() || nextLocation

  reviewBusinessLocation.value = nextLocation
  reviewBusinessLocationCoords.value = coords
  businessSearch.value = ''
  selectedBusiness.value = {
    id: String(suggestion?.placeId || ''),
    placeId: String(suggestion?.placeId || ''),
    name: businessName,
    image: '',
    meta: nextLocation,
    address: nextLocation,
    businessLat: coords?.lat ?? '',
    businessLong: coords?.lng ?? '',
    coords,
    parts: parseLocationTextParts(nextLocation),
    initial: businessName.charAt(0).toUpperCase() || 'B',
    raw: suggestion || { address: nextLocation }
  }
  closeReviewLocationPicker()
}

const openBoardModal = () => {
  boardModalOpen.value = true
  creatingBoard.value = false
  boardError.value = ''
  fetchBoards()
}

const setBoard = (board) => {
  form.boardId = board.id
  form.board = board.name
  clearError('board')
}

const selectBoard = (board) => {
  setBoard(board)
  boardModalOpen.value = false
}

const showCreateBoard = () => {
  creatingBoard.value = true
  newBoardName.value = ''
  boardError.value = ''
}

const cancelCreateBoard = () => {
  creatingBoard.value = false
  newBoardName.value = ''
  boardError.value = ''
}

const saveBoard = async () => {
  const name = newBoardName.value.trim()
  if (!name) {
    boardError.value = 'Enter board name.'
    return
  }

  boardSaving.value = true
  boardError.value = ''

  try {
    await api.addBoardFlyer({
      user_id: session.userId || session.user?.user_id || '',
      name
    })
    creatingBoard.value = false
    newBoardName.value = ''
    await fetchBoards()
  } catch (error) {
    boardError.value = error?.response?.data?.message || 'Board could not be saved. Please try again.'
  } finally {
    boardSaving.value = false
  }
}

const fetchBoards = async () => {
  boardsLoading.value = true

  try {
    const { data } = await api.listBoardsFlyer({
      user_id: session.userId || session.user?.user_id || ''
    })
    boards.value = extractBoardItems(data)
  } catch {
    boards.value = []
  } finally {
    boardsLoading.value = false
  }
}

const selectContentType = (type) => {
  form.contentTypeId = type.id
  form.contentType = type.name
  form.categoryId = type.categoryId || type.id
  clearError('contentType')
}

const fetchContentTypes = async () => {
  contentTypesLoading.value = true

  try {
    const { data } = await api.getFlyerContentTypes()
    contentTypes.value = extractContentTypeItems(data)
  } catch {
    contentTypes.value = []
  } finally {
    contentTypesLoading.value = false
  }
}

const extractCountryFromLocation = (value) => {
  const normalized = String(value || '').toLowerCase()
  if (normalized.includes('india')) return 'India'
  if (normalized.includes('united states') || normalized.includes('usa')) return 'United States'
  return value || ''
}

const mapVisibilityType = (value) => {
  const normalized = String(value || '').toLowerCase()
  if (normalized.includes('follower')) return 'followers'
  if (normalized.includes('only me')) return 'private'
  return 'public'
}

const buildAudienceCountries = (locationParts = {}) => {
  const selectedCountries = [...form.countries]
  if (form.audienceSameAsFlyer) {
    selectedCountries.push(locationParts.country_id || extractCountryFromLocation(form.flyerLocation))
  }

  return Array.from(new Set(
    selectedCountries
      .map((country) => String(country || '').trim())
      .filter(Boolean)
  )).join(',')
}

const imageFieldNames = ['image', 'image1', 'image2', 'image3', 'image4']

const getExistingImageFile = async (image, slotIndex) => {
  try {
    const response = await fetch(image.remoteUrl)
    if (!response.ok) throw new Error(`Image request failed with status ${response.status}`)
    const blob = await response.blob()
    if (!blob.size) throw new Error('The downloaded image was empty.')
    const mimeType = blob.type || 'image/jpeg'
    const extension = mimeType.split('/')[1]?.split('+')[0]?.replace('jpeg', 'jpg') || 'jpg'
    return new File([blob], `flyer-image-${slotIndex + 1}.${extension}`, { type: mimeType })
  } catch (error) {
    throw new Error(`Existing image ${slotIndex + 1} could not be retained. Please reload and try again.`, { cause: error })
  }
}

const buildFlyerImagePayload = async () => {
  const selectedImages = images.value.slice(0, imageFieldNames.length)

  if (!updateFlyerId.value) {
    return Object.fromEntries(
      imageFieldNames.map((field, index) => [field, selectedImages[index]?.file || ''])
    )
  }

  const imagePayload = Object.fromEntries(imageFieldNames.map((field) => [field, '']))
  await Promise.all(selectedImages.map(async (image) => {
    if (!Number.isInteger(image.slotIndex)) return
    const field = imageFieldNames[image.slotIndex]
    if (!field) return
    imagePayload[field] = image.file || await getExistingImageFile(image, image.slotIndex)
  }))

  return imagePayload
}

const buildFlyerPayload = async () => {
  const usesFlyerLocation = Boolean(form.audienceSameAsFlyer)
  const parsedParts = usesFlyerLocation ? parseLocationTextParts(form.flyerLocation) : emptyLocationParts()
  const coords = usesFlyerLocation
    ? flyerLocationCoords.value || {}
    : {}
  const locationParts = usesFlyerLocation
    ? mergeLocationParts(flyerLocationParts.value, parsedParts)
    : emptyLocationParts()
  const audienceCountry = buildAudienceCountries(locationParts)
  const imagePayload = await buildFlyerImagePayload()
  const payload = {
    state: locationParts.state || '',
    country_id: audienceCountry,
    title: '',
    category: form.categoryId || form.contentTypeId,
    area: locationParts.area || '',
    type: mapVisibilityType(form.visibility),
    longitude: coords.lng !== undefined && coords.lng !== null && coords.lng !== '' ? String(coords.lng) : '',
    spotted: '1',
    board_id: form.boardId,
    content_type: form.contentTypeId,
    flyer_type: creationMode.value === 'checkin' ? 'checkin' : '',
    review_count: '',
    user_id: session.userId || session.user?.user_id || '',
    ...imagePayload,
    location: usesFlyerLocation ? form.flyerLocation : '',
    end_date: form.endsDate,
    start_date: form.startsDate,
    link: '',
    review_comment: '',
    description: descriptionText() ? normalizedDescription() : '',
    locality: locationParts.locality || '',
    latitude: coords.lat !== undefined && coords.lat !== null && coords.lat !== '' ? String(coords.lat) : '',
    city: locationParts.city || '',
    street: locationParts.street || ''
  }

  return payload
}

const submitFlyer = async () => {
  submitError.value = ''
  if (!validateForm()) return

  isSubmitting.value = true
  try {
    const payload = await buildFlyerPayload()
    if (updateFlyerId.value) {
      await api.updateFlyer({
        ...payload,
        flyer_id: updateFlyerId.value
      })
    } else {
      await api.addFlyer(payload)
    }
    router.push(props.redirectTo)
  } catch (error) {
    submitError.value = error?.response?.data?.message || error?.message || 'Flyer could not be saved. Please try again.'
  } finally {
    isSubmitting.value = false
  }
}

const validateReview = () => {
  reviewErrors.rating = creationMode.value === 'review' && !reviewForm.rating
    ? 'Choose a rating from 1 to 5 stars.'
    : ''
  reviewErrors.review = descriptionText()
    ? ''
    : `Write your ${creationMode.value === 'checkin' ? 'comment' : 'review'}.`
  return Boolean(selectedBusiness.value && !reviewErrors.rating && !reviewErrors.review)
}

const buildReviewPayload = async () => {
  const imagePayload = creationMode.value === 'review'
    ? await buildFlyerImagePayload()
    : Object.fromEntries(imageFieldNames.map((field) => [field, '']))
  return {
    state: '',
    country_id: 'India,USA',
    title: '',
    category: '',
    area: '',
    type: '',
    longitude: '',
    spotted: '',
    board_id: '',
    content_type: '',
    flyer_type: creationMode.value === 'checkin' ? 'checkin' : 'review',
    review_count: creationMode.value === 'review' ? String(reviewForm.rating) : '',
    review_comment: descriptionText(),
    business_name: selectedBusiness.value?.name || '',
    business_adress: selectedBusiness.value?.address || '',
    business_lat: selectedBusiness.value?.businessLat !== undefined ? String(selectedBusiness.value.businessLat) : '',
    business_long: selectedBusiness.value?.businessLong !== undefined ? String(selectedBusiness.value.businessLong) : '',
    business_id: selectedBusiness.value?.id || '',
    checkin_date: creationMode.value === 'checkin' ? (props.initialFlyer?.checkin_date || props.initialFlyer?.postedAt || '') : '',
    user_id: session.userId || session.user?.user_id || '',
    ...imagePayload,
    location: '',
    end_date: '',
    start_date: '',
    link: '',
    description: '',
    locality: '',
    latitude: '',
    city: '',
    street: ''
  }
}

const submitReview = async () => {
  reviewSubmitError.value = ''
  if (!validateReview()) return

  isReviewSubmitting.value = true
  try {
    const payload = await buildReviewPayload()
    if (updateFlyerId.value) {
      await api.updateFlyer({
        ...payload,
        flyer_id: updateFlyerId.value
      })
    } else {
      await api.addFlyer(payload)
    }
    router.push(props.redirectTo)
  } catch (error) {
    const postName = creationMode.value === 'checkin' ? 'Checkin' : 'Review'
    reviewSubmitError.value = error?.response?.data?.message || error?.message || `${postName} could not be posted. Please try again.`
  } finally {
    isReviewSubmitting.value = false
  }
}

const submitCreateChoice = () => {
  if (isFlyerMode.value) return submitFlyer()
  return submitReview()
}

onMounted(() => {
  hydrateInitialFlyer()
  if (!isUpdateMode.value) fetchCheckinOption()
  fetchBoards()
  fetchContentTypes()
})

onBeforeUnmount(() => {
  images.value.forEach((image) => {
    if (image.file && image.src?.startsWith('blob:')) URL.revokeObjectURL(image.src)
  })
})

watch(() => props.initialFlyer, hydrateInitialFlyer, { deep: true })

watch(() => form.description, () => {
  if (errors.description) validateField('description')
  if (reviewErrors.review && descriptionText()) reviewErrors.review = ''
})

watch(images, () => {
  if (errors.description) validateField('description')
}, { deep: true })

watch(() => form.flyerLocation, () => {
  if (errors.flyerLocation) validateField('flyerLocation')
})

watch(() => [form.startsDate, form.endsDate], () => {
  if (form.startsDate && form.endsDate && form.endsDate < form.startsDate) {
    form.endsDate = form.startsDate
    return
  }
  if (errors.dates || form.startsDate || form.endsDate) validateField('dates')
})

watch(creationMode, (mode) => {
  submitError.value = ''
  reviewSubmitError.value = ''
  if (['review', 'checkin'].includes(mode) && !isUpdateMode.value) {
    if (!businesses.value.length && !businessesLoading.value) fetchBusinesses('')
  }
})

watch(() => reviewForm.rating, () => {
  if (reviewErrors.rating) reviewErrors.rating = ''
})

const ChevronIcon = defineComponent({
  setup() {
    return () =>
      h('svg', { class: 'h-6 w-6 shrink-0 text-slate-500', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2.5', 'stroke-linecap': 'round', 'aria-hidden': 'true' }, [
        h('path', { d: 'm6 9 6 6 6-6' })
      ])
  }
})

const BottomSheet = defineComponent({
  props: {
    title: { type: String, required: true },
    saveLabel: { type: String, default: '' }
  },
  emits: ['close', 'save'],
  setup(props, { emit, slots }) {
    return () =>
      h('div', { class: 'fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-slate-950/55 px-3 py-4 sm:px-6 sm:py-6' }, [
        h('div', { class: 'flex max-h-[calc(100svh-2rem)] w-full max-w-sm flex-col overflow-hidden rounded-[1.5rem] bg-white shadow-2xl sm:max-h-[80vh] sm:max-w-md lg:max-w-lg' }, [
          h('header', { class: 'shrink-0 flex items-center justify-between border-b border-slate-200 px-4 py-3' }, [
            h('button', { class: 'grid h-10 w-10 place-items-center rounded-full text-slate-700 hover:bg-slate-100', type: 'button', onClick: () => emit('close'), 'aria-label': 'Close' }, [
              h('svg', { class: 'h-6 w-6', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2.5', 'stroke-linecap': 'round' }, [
                h('path', { d: 'M18 6 6 18' }),
                h('path', { d: 'm6 6 12 12' })
              ])
            ]),
            h('h2', { class: 'm-0 text-xl font-medium text-slate-950' }, props.title),
            props.saveLabel
              ? h('button', { class: 'h-10 rounded-full px-3 text-base font-medium text-blue-700', type: 'button', onClick: () => emit('save') }, props.saveLabel)
              : h('span', { class: 'h-10 w-10' })
          ]),
          h('div', { class: 'min-h-0 flex-1 overflow-y-auto pb-3' }, slots.default?.())
        ])
      ])
  }
})
</script>

<style scoped>
.create-flyer-page {
  min-height: 100vh;
  background: #f4f5fc;
  padding: 1.25rem 1rem 2rem;
}

.create-flyer-shell {
  margin: 0 auto;
  width: 100%;
  max-width: 36rem;
}

.create-flyer-header {
  position: relative;
  display: flex;
  justify-content: center;
  padding: 1.25rem 0 1.5rem;
}

.create-flyer-back {
  position: absolute;
  left: 0;
  top: 50%;
  display: grid;
  height: 2.5rem;
  width: 2.5rem;
  place-items: center;
  transform: translateY(-50%);
  border-radius: 9999px;
  color: rgb(15 23 42);
}

.create-flyer-body {
  display: grid;
  gap: 1.2rem;
}

.creation-type-card {
  margin-bottom: 1.2rem;
  padding: 0;
}

.creation-type-placeholder {
  display: block;
  width: 100%;
  max-height: 19rem;
  margin-bottom: 1.25rem;
  object-fit: contain;
}

.creation-type-title {
  margin-bottom: 1rem;
  font-size: 1rem;
  font-weight: 400;
  color: rgb(100 116 139);
}

.creation-type-options {
  display: grid;
  gap: 0.75rem;
}

.creation-type-option {
  display: flex;
  min-height: 5rem;
  cursor: pointer;
  align-items: center;
  gap: 0.85rem;
  border: 1px solid rgb(226 232 240);
  border-radius: 1rem;
  background: white;
  padding: 0.9rem 1rem;
  box-shadow: 0 2px 8px rgb(15 23 42 / 4%);
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.creation-type-option:hover {
  border-color: rgb(191 219 254);
  box-shadow: 0 5px 16px rgb(37 99 235 / 8%);
}

.creation-type-icon {
  display: grid;
  height: 2.65rem;
  width: 2.65rem;
  flex-shrink: 0;
  place-items: center;
  border-radius: 0.85rem;
  background: rgb(239 246 255);
  color: rgb(37 99 235);
}

.creation-type-icon svg {
  height: 1.35rem;
  width: 1.35rem;
}

.creation-type-label,
.creation-type-help {
  display: block;
}

.creation-type-chevron {
  height: 1.25rem;
  width: 1.25rem;
  flex-shrink: 0;
  color: rgb(71 85 105);
}

.creation-type-label {
  font-size: 1rem;
  font-weight: 500;
  color: rgb(15 23 42);
}

.creation-type-help {
  margin-top: 0.2rem;
  font-size: 0.8rem;
  font-weight: 400;
  color: rgb(100 116 139);
}

.business-list {
  overflow: hidden;
  border: 1px solid rgb(226 232 240);
  border-radius: 1rem;
}

.business-search-field {
  position: relative;
  display: flex;
  min-height: 2.9rem;
  align-items: center;
  border-radius: 0.75rem;
  background: rgb(226 232 240);
  color: rgb(100 116 139);
}

.business-search-field > svg {
  position: absolute;
  left: 0.8rem;
  height: 1.15rem;
  width: 1.15rem;
  pointer-events: none;
}

.business-search-field input {
  min-width: 0;
  flex: 1;
  border: 0;
  background: transparent;
  padding: 0.65rem 2.7rem 0.65rem 2.45rem;
  font-size: 1rem;
  font-weight: 400;
  color: rgb(15 23 42);
  outline: none;
}

.business-search-field input::-webkit-search-cancel-button {
  display: none;
}

.business-search-field button {
  position: absolute;
  right: 0.65rem;
  display: grid;
  height: 1.6rem;
  width: 1.6rem;
  place-items: center;
  border-radius: 9999px;
  background: rgb(100 116 139);
  color: white;
}

.business-search-field button svg {
  height: 0.8rem;
  width: 0.8rem;
}

.business-list-heading {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  border-radius: 0.85rem;
  padding: 1rem 0.45rem 0.75rem;
  transition: background 0.16s ease;
}

.business-list-heading:hover {
  background: rgb(248 250 252);
}

.business-list-heading-icon {
  display: grid;
  height: 1.7rem;
  width: 1.7rem;
  place-items: center;
  color: rgb(37 99 235);
}

.business-list-heading-icon svg {
  height: 1.45rem;
  width: 1.45rem;
}

.business-row {
  display: flex;
  min-height: 4.75rem;
  cursor: pointer;
  align-items: center;
  gap: 0.85rem;
  border-bottom: 1px solid rgb(226 232 240);
  padding: 0.8rem 0.9rem;
}

.business-row:last-child {
  border-bottom: 0;
}

.business-row:hover {
  background: rgb(248 250 252);
}

.business-avatar {
  display: grid;
  height: 3rem;
  width: 3rem;
  flex-shrink: 0;
  overflow: hidden;
  place-items: center;
  border-radius: 0.9rem;
  background: rgb(219 234 254);
  font-size: 1rem;
  font-weight: 600;
  color: rgb(29 78 216);
}

.business-avatar img {
  height: 100%;
  width: 100%;
  object-fit: cover;
}

.business-avatar-outline {
  overflow: hidden;
  border-radius: 0;
  background: transparent;
  color: rgb(37 99 235);
}

.business-avatar-outline svg {
  height: 2.65rem;
  width: 2.65rem;
}

.business-row-skeleton {
  cursor: default;
  border: 1px solid rgb(241 245 249);
  border-radius: 1rem;
}

.business-avatar-skeleton,
.business-line-skeleton {
  display: block;
  border-radius: 0.65rem;
  background: linear-gradient(90deg, rgb(241 245 249), rgb(226 232 240), rgb(241 245 249));
  background-size: 200% 100%;
  animation: review-skeleton 1.4s ease-in-out infinite;
}

.business-avatar-skeleton {
  height: 3rem;
  width: 3rem;
  flex-shrink: 0;
}

.business-line-skeleton {
  height: 0.75rem;
  width: 55%;
}

.business-line-skeleton-wide {
  width: 75%;
}

.review-state-message {
  border-radius: 1rem;
  background: rgb(248 250 252);
  padding: 1.25rem;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 400;
  color: rgb(100 116 139);
}

.review-state-error {
  background: rgb(254 242 242);
  color: rgb(185 28 28);
}

.selected-business-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.review-form-step {
  gap: 0.8rem;
}

.review-form-step .surface-card {
  border-radius: 0.85rem;
  padding: 1rem;
  box-shadow: 0 1px 5px rgb(15 23 42 / 4%);
}

.selected-business-card .link-button {
  align-self: center;
  margin-bottom: 0;
}

.review-rating-card {
  padding-block: 0.9rem;
}

.review-rating-fieldset {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;
}

.review-rating-fieldset .inline-error {
  width: 100%;
  margin-top: 0;
}

.rating-stars {
  display: flex;
  gap: 0.45rem;
  margin-top: 0;
}

.rating-star {
  cursor: pointer;
  color: rgb(203 213 225);
}

.rating-star svg {
  height: 1.8rem;
  width: 1.8rem;
  fill: none;
  transition: color 0.15s ease, fill 0.15s ease, transform 0.15s ease;
}

.rating-star:hover svg {
  transform: scale(1.08);
}

.rating-star-active {
  color: rgb(245 158 11);
}

.rating-star-active svg {
  fill: currentColor;
}

.review-label {
  display: block;
  margin-bottom: 0.7rem;
  font-size: 1rem;
  font-weight: 500;
  color: rgb(15 23 42);
}

@keyframes review-skeleton {
  to { background-position: -200% 0; }
}

.surface-card {
  border-radius: 1.5rem;
  background: #fff;
  padding: 1.25rem;
}

.upload-card {
  padding-block: 1.5rem;
}

.add-photo-button {
  min-height: 2.75rem;
  border-radius: 9999px;
  background: rgb(59 130 246);
  padding: 0 1.25rem;
  font-size: 1rem;
  font-weight: 500;
  color: white;
  transition: background 0.16s ease, transform 0.16s ease;
}

.add-photo-button:hover {
  background: rgb(37 99 235);
}

.inline-error {
  display: block;
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  font-weight: 400;
  line-height: 1.35;
  color: rgb(220 38 38);
}

.content-section {
  display: grid;
  gap: 0.7rem;
}

.section-label {
  margin: 0;
  padding: 0 0.125rem;
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgb(15 23 42);
}

.surface-stack {
  padding: 0;
  overflow: hidden;
}

.surface-stack > * + * {
  border-top: 1px solid rgb(226 232 240);
}

.detail-row,
.detail-row-static {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 1rem;
  padding: 1.15rem 1.25rem;
  text-align: left;
}

.row-icon-square {
  display: grid;
  height: 2.5rem;
  width: 2.5rem;
  flex-shrink: 0;
  place-items: center;
  border-radius: 0.9rem;
  background: rgb(248 250 252);
  color: rgb(37 99 235);
}

.link-button {
  align-self: flex-end;
  margin-bottom: 0.1rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: rgb(37 99 235);
}

.audience-chip-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
}

.audience-chip {
  min-height: 2.85rem;
  min-width: 0;
  border-radius: 0.75rem;
  background: rgb(234 236 244);
  padding: 0.65rem 0.35rem;
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.15;
  color: rgb(15 23 42);
  overflow-wrap: anywhere;
}

.audience-chip-active {
  background: rgb(219 234 254);
  color: rgb(29 78 216);
}

.post-button {
  width: 100%;
  min-height: 3.9rem;
  border-radius: 0.95rem;
  background: rgb(59 130 246);
  font-size: 1.05rem;
  font-weight: 500;
  color: white;
  transition: background 0.16s ease, transform 0.16s ease;
}

.post-button:hover:not(:disabled) {
  background: rgb(37 99 235);
}

.post-button:disabled {
  cursor: not-allowed;
  opacity: 0.72;
}

.field-label {
  display: grid;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 400;
  color: rgb(71 85 105);
}

.clear-date-link {
  border: 0;
  background: transparent;
  padding: 0;
  font-size: 0.78rem;
  font-weight: 500;
  color: rgb(225 29 72);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.field-input {
  min-height: 3rem;
  border-radius: 1rem;
  border: 1px solid rgb(203 213 225);
  padding: 0 1rem;
  font-weight: 400;
  outline: none;
}

.location-search-field {
  position: relative;
}

.location-search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  display: grid;
  height: 1.5rem;
  width: 1.5rem;
  place-items: center;
  transform: translateY(-50%);
  color: rgb(59 130 246);
  pointer-events: none;
}

.location-search-icon svg {
  height: 1.5rem;
  width: 1.5rem;
}

.location-search-input {
  width: 100%;
  padding-left: 3.25rem;
}

.location-search-input::placeholder {
  color: rgb(148 163 184);
}

.modal-choice,
.modal-list-row {
  display: flex;
  min-height: 3.35rem;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  border-bottom: 1px solid rgb(226 232 240);
  padding: 0.62rem 0.85rem;
  text-align: left;
  font-size: 0.95rem;
  font-weight: 400;
  color: rgb(15 23 42);
}

.modal-list-row:last-child {
  border-bottom: 0;
}

.modal-list-row:hover {
  background: rgb(248 250 252);
}

.location-suggestion {
  display: flex;
  min-height: 4.5rem;
  width: 100%;
  align-items: center;
  gap: 0.9rem;
  border-bottom: 1px solid rgb(226 232 240);
  padding: 0.85rem 1rem;
  text-align: left;
}

.location-suggestion:last-child {
  border-bottom: 0;
}

.location-suggestion:hover {
  background: rgb(248 250 252);
}

.modal-secondary,
.modal-primary {
  min-height: 2.75rem;
  border-radius: 0.8rem;
  padding: 0 1.1rem;
  font-size: 0.92rem;
  font-weight: 500;
}

.modal-secondary {
  background: rgb(239 248 255);
  color: rgb(15 23 42);
}

.modal-primary {
  background: rgb(37 99 235);
  color: white;
}

.modal-primary:hover:not(:disabled) {
  background: rgb(29 78 216);
}

.compact-modal-action {
  min-height: 2.35rem;
  border-radius: 0.75rem;
}

@media (max-width: 640px) {
  .create-flyer-page {
    padding-inline: 0.85rem;
  }

  .create-flyer-header {
    padding-top: 0.75rem;
  }

  .create-flyer-header h1 {
    font-size: 1.45rem;
  }

  .surface-card {
    border-radius: 1.2rem;
    padding: 1rem;
  }

  .creation-type-card {
    padding: 0;
  }

  .audience-chip-grid {
    gap: 0.45rem;
  }
}

@media (min-width: 640px) {
  .audience-chip-grid {
    gap: 0.75rem;
  }

  .audience-chip {
    min-height: 3.25rem;
    padding: 0.75rem 0.5rem;
    font-size: 0.95rem;
  }
}
</style>
